import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  LogOut,
  ChevronDown,
  Home,
  Users,
  Briefcase,
  Key,
  Tag,
  Ticket,
  Smartphone,
  LayoutDashboard,
  Store,
  UserCog,
  ChevronRight,
  Sun,
  Moon,
  DollarSign,
  BarChart3,
} from "lucide-react";
import { useTheme } from "../theme/ThemeProvider";
import { hasPermission, Permissoes } from "../utils/permissoes";

const cx = (...arr) => arr.filter(Boolean).join(" ");


const ActivePill = ({ active }) => (
  <span
    className={cx(
      "absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1.5 rounded-r-full transition",
      active ? "bg-blue-600 dark:bg-blue-400" : "bg-transparent"
    )}
  />
);

const NavLinkItem = ({ to, icon, label, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={cx(
      "relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition",
      active
        ? "bg-blue-50 text-blue-800 ring-1 ring-blue-100 dark:bg-white/10 dark:text-white dark:ring-white/10"
        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
    )}
  >
    <ActivePill active={active} />
    <span className={cx("shrink-0", active ? "text-blue-700 dark:text-blue-200" : "text-gray-500 dark:text-slate-300")}>
      {icon}
    </span>
    <span className="truncate">{label}</span>
    {active ? <ChevronRight className="ml-auto text-blue-400 dark:text-blue-200" size={18} /> : null}
  </Link>
);

const NavGroup = ({ item, location, isOpen, setOpen, onNavigate }) => {
  const groupActive = useMemo(
    () => item.children.some((c) => location.pathname.startsWith(c.path)),
    [item.children, location.pathname]
  );
  

  return (
    <div className="space-y-1">
      <button
        onClick={() => setOpen(!isOpen)}
        className={cx(
          "w-full relative flex justify-between items-center px-3 py-2.5 text-sm font-semibold text-left rounded-xl transition",
          groupActive
            ? "bg-gray-100 text-gray-900 dark:bg-white/10 dark:text-white"
            : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
        )}
      >
        <div className="flex items-center gap-3">
          <span className={cx("shrink-0", groupActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-slate-300")}>
            {item.icon}
          </span>
          <span className="truncate">{item.title}</span>
        </div>

        <ChevronDown size={18} className={cx("transition-transform", isOpen ? "rotate-180" : "")} />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="pl-3 pr-1 py-1">
              <div className="border-l border-gray-200 dark:border-white/10 pl-3 space-y-1">
                {item.children.map((child) => {
                  const active =
                    location.pathname === child.path ||
                    location.pathname.startsWith(child.path + "/");

                  return (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={onNavigate}
                      className={cx(
                        "flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition",
                        active
                          ? "bg-blue-50 text-blue-800 ring-1 ring-blue-100 dark:bg-white/10 dark:text-white dark:ring-white/10"
                          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
                      )}
                    >
                      <span className={cx("text-gray-400 dark:text-slate-300", active ? "text-blue-500 dark:text-blue-200" : "")}>
                        {child.icon ?? <ChevronRight size={16} />}
                      </span>
                      <span className="truncate">{child.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Sidebar = ({ userName, onLogout }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  const navItems = [
    {
      key: "licenciamento",
      title: "Licenciamento",
      icon: <Key size={18} />,
      children: [
        { label: "ZenPDV Desktop", path: "/licenciamento/zenpdv", icon: <Key size={16} />, permission: Permissoes.LicencaVisualizar },
        { label: "ZenTag", path: "/licenciamento/zentag/list", icon: <Tag size={16} />, permission: Permissoes.LicencaVisualizar },
        { label: "ZenPass", path: "/licenciamento/zenpass/list", icon: <Ticket size={16} />, permission: Permissoes.LicencaVisualizar },
        { label: "ZenMobile", path: "/licenciamento/zenmobile/list", icon: <Smartphone size={16} />, permission: Permissoes.LicencaVisualizar },
        { label: "ZenGestão", path: "/licenciamento/zengestao/list", icon: <LayoutDashboard size={16} />, permission: Permissoes.LicencaVisualizar },
      ],
    },
    {
      key: "clientes",
      title: "Clientes",
      icon: <Briefcase size={18} />,
      children: [
        { label: "Clientes", path: "/clientes", icon: <Briefcase size={16} />, permission: Permissoes.ClienteVisualizar },
        { label: "Novo Cliente", path: "/clientes/novo", icon: <ChevronRight size={16} />, permission: Permissoes.ClienteCadastrar },
      ],
    },

    {
      key: "vendedores",
      title: "Vendedores",
      icon: <Users size={18} />,
      children: [
        { label: "Vendedores", path: "/vendedores", icon: <Users size={16} /> },
        { label: "Novo Vendedor", path: "/vendedores/novo", icon: <ChevronRight size={16} /> },
      ],
    },

    {
      key: "precos",
      title: "Preços",
      icon: <DollarSign size={18} />,
      children: [
        { label: "Tabelas de preço", path: "/tabelas-preco", icon: <DollarSign size={16} />, permission: Permissoes.LicencaEditar },
        { label: "Nova tabela", path: "/tabelas-preco/novo", icon: <ChevronRight size={16} />, permission: Permissoes.LicencaEditar },
      ],
    },
    {
      key: "relatorios",
      title: "Relatórios",
      icon: <BarChart3 size={18} />,
      children: [
        { label: "Licenças e faturamento", path: "/relatorios/licencas", icon: <BarChart3 size={16} />, permission: Permissoes.RelatorioAcessar },
        { label: "Faturamento distribuidor", path: "/relatorios/distribuidor", icon: <BarChart3 size={16} />, permission: Permissoes.RelatorioAcessar },
        { label: "Faturamento revenda", path: "/relatorios/revenda", icon: <BarChart3 size={16} />, permission: Permissoes.RelatorioAcessar },
      ],
    },
    {
      key: "auditoria",
      title: "Auditoria",
      icon: <BarChart3 size={18} />,
      children: [
        { label: "Logs da API", path: "/auditoria/api-logs", icon: <BarChart3 size={16} />, permission: Permissoes.AuditoriaAcessar },
      ],
    },
    {
      key: "distribuidores",
      title: "Distribuidores",
      icon: <Store size={18} />,
      children: [
        { label: "Distribuidores", path: "/distribuidores", icon: <Store size={16} /> },
        { label: "Novo distribuidor", path: "/distribuidores/novo", icon: <ChevronRight size={16} /> },
        { label: "Usuários do Distribuidor", path: "/distribuidores/usuarios", icon: <Users size={16} /> },
      ],
    },
    {
      key: "revendas",
      title: "Revendas",
      icon: <Store size={18} />,
      children: [
        { label: "Revendas", path: "/revendas", icon: <Store size={16} />, permission: "revenda.ver" },
        { label: "Nova Revenda", path: "/revendas/novo", icon: <ChevronRight size={16} />, permission: "revenda.cadastrar" },
        { label: "Usuários da Revenda", path: "/revendas/usuarios", icon: <Users size={16} />, permission: "usuario.ver" },
      ],
    },
    {
      key: "usuarios",
      title: "Usuários",
      icon: <UserCog size={18} />,
      children: [
        { label: "Listar Usuários", path: "/usuarios", icon: <Users size={16} />, permission: "usuario.ver" },
        { label: "Novo Usuário", path: "/usuarios/novo", icon: <ChevronRight size={16} />, permission: "usuario.cadastrar" },
      ],
    },
  ];

  const token = sessionStorage.getItem("token");

  const visibleNavItems = useMemo(() => {
    return navItems
      .map((group) => ({
        ...group,
        children: group.children.filter(
          (item) => !item.permission || hasPermission(token, item.permission)
        ),
      }))
      .filter((group) => group.children.length > 0);
  }, [token, location.pathname]);

  const activeGroupKey = useMemo(() => {
    const found = visibleNavItems.find((g) => g.children.some((c) => location.pathname.startsWith(c.path)));
    return found?.key ?? null;
  }, [location.pathname, visibleNavItems]);

  const [openGroup, setOpenGroup] = useState(activeGroupKey);

  useEffect(() => {
    if (activeGroupKey) setOpenGroup(activeGroupKey);
  }, [activeGroupKey]);

  const closeMobile = () => setSidebarOpen(false);

  const SidebarContent = () => (
    <div
      className={cx(
        "h-full flex flex-col border-r",
        // light
        "bg-white text-gray-800 border-gray-200",
        // dark gradient (igual vibe do login)
        "dark:border-white/10 dark:text-white dark:bg-gradient-to-b dark:from-[#0f172a] dark:via-[#1e293b] dark:to-[#020617]"
      )}
    >
      {/* header */}
      <div className="px-4 pt-5 pb-4 border-b border-gray-100 dark:border-white/10">
        <div className="flex items-center justify-content-center">
          <Link to="/home" className="flex items-center gap-3">
            <img
              src={isDark ? "/logo-branco.png" : "/logo.png"}
              alt="Zen Portal"
              className="h-12 w-auto"
            />
          </Link>



          <button
            className="md:hidden text-gray-600 hover:text-gray-900 dark:text-slate-200 dark:hover:text-white p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
            onClick={closeMobile}
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex items-center justify-content-center text-xs mt-2 text-gray-500 dark:text-slate-30">Licenciamento & Gestão</div>

        {/* user card */}
        <div className="mt-4 rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50 to-white p-3 dark:border-white/10 dark:from-white/10 dark:to-white/5">
          <div className="text-xs text-gray-500 dark:text-slate-300">Logado como</div>
          <div className="font-semibold text-gray-900 dark:text-white truncate">
            {userName || "Usuário"}
          </div>

          {/* BOTÃO TEMA (aqui embaixo do usuário) */}
          <button
            onClick={toggleTheme}
            className={cx(
              "mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition border",
              "border-gray-200 text-gray-800 hover:bg-gray-50",
              "dark:border-white/10 dark:text-white dark:hover:bg-white/10"
            )}
            title={isDark ? "Mudar para tema claro" : "Mudar para tema escuro"}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
            <span>{isDark ? "Tema claro" : "Tema escuro"}</span>
          </button>
        </div>
      </div>

      {/* nav */}
      <nav className="flex-1 px-3 py-4 space-y-3 overflow-y-auto">
        <NavLinkItem
          to="/home"
          icon={<Home size={18} />}
          label="Dashboard"
          active={location.pathname === "/home"}
          onClick={closeMobile}
        />

        <div className="pt-1">
          <div className="px-2 text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-300/70 mb-2">
            Navegação
          </div>

          <div className="space-y-2">
            {visibleNavItems.map((item) => (
              <NavGroup
                key={item.key}
                item={item}
                location={location}
                isOpen={openGroup === item.key}
                setOpen={(v) => setOpenGroup(v ? item.key : null)}
                onNavigate={closeMobile}
              />
            ))}
          </div>
        </div>
      </nav>

      {/* footer */}
      <div className="px-3 py-4 border-t border-gray-100 dark:border-white/10">
        <button
          onClick={onLogout}
          className={cx(
            "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition border",
            "border-red-200 text-red-700 hover:bg-red-50",
            "dark:border-red-500/20 dark:text-red-200 dark:hover:bg-red-500/10"
          )}
        >
          <LogOut size={18} />
          <span>Sair</span>
        </button>

        <div className="mt-3 text-[11px] text-gray-400 dark:text-slate-300/70 text-center">
          ZenSoftware • {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="w-72 flex flex-col">
          <SidebarContent />
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="md:hidden sticky top-0 z-30 px-3 pt-3">
        <button
          onClick={() => setSidebarOpen(true)}
          className="inline-flex items-center justify-center h-10 w-10 rounded-xl border transition
               bg-white/80 backdrop-blur hover:bg-white
               border-gray-200 text-gray-800
               dark:bg-white/10 dark:hover:bg-white/15 dark:border-white/10 dark:text-white"
          aria-label="Abrir menu"
        >
          <Menu size={22} />
        </button>
      </div>


      {/* Mobile overlay + drawer */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobile}
            />


            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="fixed inset-y-0 left-0 w-72 z-50 md:hidden"
            >
              <SidebarContent />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
